import { useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Field, FormSpy } from 'react-final-form';

import { IKnmSubjectRef } from 'KnmPackage/KnmSubjectModule/components/KnmSubject';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Button } from 'UIPackage/MainModule/components/Button';
import { ORGANIZATION_BLOCK_ID } from 'DecisionPackage/MainModule/constants/decisionFieldsElementsIds';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { APPLICATION_CONDITION_SUBJECT_REQUIRED } from 'CorePackage/MainModule/constants/validationErrors';
import {
  getDecisionIsNew,
  getDecisionTargetEntitySelectOptions,
  getKnmIsDecisionStatusFillInProcess,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionOrganizationForm } from 'DecisionPackage/MainModule/interfaces/IDecisionOrganizationForm';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { KNM_VERIFIED_PERSON } from 'CorePackage/MainModule/routes/routes';

import { DecisionSubjectItem } from './DecisionSubjectItem';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

const getOrganizationBlockId = (id: string) => `organization.${id}`;

interface IDecisionOrganizationProps {
  formRef?: IDecisionFormRef;
  isNew?: boolean;
  isWatchOnly: boolean;
  organization?: IDecisionOrganizationForm;
  isPassportByCheckPrescriptionOldRules: boolean;
}

interface IDecisionOrganizationStoreProps {
  isDecisionStatusFillInProcess: boolean;
  isNewDecision: boolean;
  isSupervisor: boolean;
  targetEntitySelectOptions?: IDictionarySelectOption<ITargetEntity>[];
}

const DecisionOrganizationWithoutStore = ({
  formRef,
  isNew,
  isWatchOnly,
  organization,
  isDecisionStatusFillInProcess,
  isNewDecision,
  isSupervisor,
  targetEntitySelectOptions,
  isPassportByCheckPrescriptionOldRules,
}: IDecisionOrganizationProps & IDecisionOrganizationStoreProps) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<boolean>(true);
  const organizationItemRef = useRef<IKnmSubjectRef>(null);
  const reasons = formRef?.current.getFieldState('reasons')?.value;

  const isConnectedToErvkObject =
    !!organization?.ervkId &&
    !!formRef?.current
      .getFieldState('objects')
      ?.value?.flatMap(object => object.organizationErvkId ?? [])
      ?.find(x => x === organization?.ervkId);

  const isFilledFromLink = !!reasons?.[0]?.links?.length && !isPassportByCheckPrescriptionOldRules;
  const isEditDisabledByRoleOrStatus = !isSupervisor || (!isNewDecision && !isDecisionStatusFillInProcess);

  const isDisableOrganization = isConnectedToErvkObject || isEditDisabledByRoleOrStatus || isFilledFromLink;
  const isRemoveAvailable = !isWatchOnly && !isEditDisabledByRoleOrStatus && !isConnectedToErvkObject;

  const handleClose = useCallback(() => {
    organizationItemRef.current?.onClear();
    formRef?.current.change('organization');
    formRef?.current.change('isOrganizationNew');
  }, [formRef]);

  const handleAdd = useCallback(() => {
    // @ts-ignore
    formRef?.current.change('organization', {});
    formRef?.current.change('isOrganizationNew', true);
  }, [formRef]);

  const hasError = checkHasErrorInCollapseBlock(formRef, 'organization', ['act']);

  return (
    <div id={KNM_VERIFIED_PERSON} style={{ marginBottom: '16px' }}>
      <Section
        title={getTitleFieldWithRedStar({
          label: 'Сведения о контролируемом лице',
          field: `organization`,
          sectionName: 'organization',
          isDecision: true,
        })}
        type="list"
        id={ORGANIZATION_BLOCK_ID}
        customButton={
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            disabled={!!organization || isWatchOnly || isDisableOrganization}
            onClick={handleAdd}
          />
        }
      >
        <Field name={'organization'}>{() => null}</Field>
        {organization && (
          <>
            <FormSpy subscription={{ errors: true }}>
              {props => {
                const fieldError = props.errors?.organization;

                const errorMessage: string | undefined = fieldError
                  ? (Object.values(fieldError).reduce((prev: string, curr: string) => prev + curr, '') as string)
                  : undefined;

                if (errorMessage && errorMessage === APPLICATION_CONDITION_SUBJECT_REQUIRED) {
                  return <p className="shared-input-error shared-row__small-left-margin">{errorMessage}</p>;
                }

                return null;
              }}
            </FormSpy>
            <KnmCollapse
              id="organization"
              title="Сведения о контролируемом лице"
              isNew={isNew}
              onClose={isRemoveAvailable ? handleClose : undefined}
              openByDefault={isNew}
              error={knmCollapseOpened && hasError}
              changeOpenedCallback={setKnmCollapseOpened}
            >
              <DecisionSubjectItem
                ervkId={organization?.ervkId}
                checkSubjectResult={organization?.checkSubjectResult}
                formRef={formRef}
                getBlockId={getOrganizationBlockId}
                inn={organization?.inn}
                isFiz={false}
                isIt={organization.isPresentInItRegistry}
                isSonko={organization?.isSonko}
                itAccreditationResponse={organization.itAccreditationResponse}
                kpp={organization?.kpp}
                mspCode={organization?.mspCode}
                mspRegistryDate={organization?.mspRegistryDate}
                name="organization"
                ogrn={organization?.ogrn}
                organizationType={organization.organizationType}
                rafpNza={organization?.rafpNza}
                ref={organizationItemRef}
                smevRequestStatus={organization?.smevRequestStatus}
                snils={organization?.snils}
                subjectTypeOptions={targetEntitySelectOptions}
                isWatchOnly={isWatchOnly || isDisableOrganization}
                subjectTypeLabel="Тип контролируемого лица"
                organizationAutocomplete={organization.isAutocomplete}
                smevItAccreditationRequestStatus={organization.smevItAccreditationRequestStatus}
              />
            </KnmCollapse>
          </>
        )}
        <FormSpy subscription={{ errors: true, touched: true }}>
          {props => {
            if (
              props?.touched?.organization &&
              props?.errors?.organization &&
              typeof props.errors?.organization?.[0] === 'string'
            ) {
              const fieldError = props.errors?.organization;
              const errorMessage: string | undefined = fieldError
                ? (Object.values(fieldError).reduce((prev: string, curr: string) => prev + curr, '') as string)
                : undefined;

              if (errorMessage && errorMessage !== APPLICATION_CONDITION_SUBJECT_REQUIRED) {
                return <p className="shared-input-error">{errorMessage}</p>;
              }
            }

            return null;
          }}
        </FormSpy>
      </Section>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IDecisionOrganizationStoreProps => ({
  isDecisionStatusFillInProcess: getKnmIsDecisionStatusFillInProcess(state),
  isNewDecision: getDecisionIsNew(state) || false,
  isSupervisor: getUserRoleIsSupervisor(state),
  targetEntitySelectOptions: getDecisionTargetEntitySelectOptions(state),
});

export const DecisionOrganization = connect(mapStateToProps)(DecisionOrganizationWithoutStore);
